// =============================================================================
// Gift Cards — faithful port of POS GiftcardDashboardView / frmGiftCertificates.
// List + balance lookup by certificate number.
// =============================================================================
import { useEffect, useState } from 'react'
import { Search, Plus, X } from 'lucide-react'
import {
  listGiftCertificates, lookupGiftCertificate, createGiftCertificate, updateGiftCertificate, listCustomers,
} from '../services/data'
import { useAuth } from '../context/AuthContext'
import { PageHeader, Card, DataTable, SearchInput, Pagination, Spinner, Button, FilterBar } from '../components/shared/ui'
import { currency, date, customerLabel } from '../lib/format'
import { useServerList } from '../lib/list'
import type { GiftCertificate, Customer } from '../types'
import { toast } from 'react-toastify'

const PAGE_SIZE = 25

export default function GiftCards() {
  const [lookup, setLookup] = useState('')
  const [found, setFound] = useState<GiftCertificate | null | undefined>(undefined)
  const [editing, setEditing] = useState<GiftCertificate | 'new' | null>(null)
  const { rows, count, page, search, sort, loading, setPage, setSearch, setFilters, toggleSort, reload } =
    useServerList<GiftCertificate>(listGiftCertificates, { pageSize: PAGE_SIZE, defaultSort: { column: 'issue_date', dir: 'desc' } })

  const doLookup = async () => {
    if (!lookup.trim()) return
    try {
      const gc = await lookupGiftCertificate(lookup)
      setFound(gc)
      if (!gc) toast.info('No gift certificate found with that number')
    } catch { toast.error('Lookup failed') }
  }

  return (
    <div>
      <PageHeader title="Gift Cards" subtitle={`${count.toLocaleString()} certificates`}
        actions={<Button onClick={() => setEditing('new')}><Plus size={16} /> Issue Gift Certificate</Button>} />

      <Card className="p-4 mb-4">
        <h3 className="font-semibold text-gray-800 mb-3">Check Balance</h3>
        <div className="flex gap-2 items-center">
          <input value={lookup} onChange={e => setLookup(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') doLookup() }}
            placeholder="Swipe or enter certificate number…"
            className="flex-1 max-w-md px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm" />
          <Button onClick={doLookup}><Search size={16} /> Look Up</Button>
        </div>
        {found && (
          <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200 flex flex-wrap gap-6">
            <div><p className="text-xs text-gray-500">Certificate</p><p className="font-semibold">{found.certificate_number}</p></div>
            <div><p className="text-xs text-gray-500">Current Balance</p><p className="font-semibold text-green-700 text-lg">{currency(found.current_balance)}</p></div>
            <div><p className="text-xs text-gray-500">Original</p><p className="font-semibold">{currency(found.original_amount)}</p></div>
            <div><p className="text-xs text-gray-500">Issued</p><p className="font-semibold">{date(found.issue_date)}</p></div>
            <div><p className="text-xs text-gray-500">For</p><p className="font-semibold">{found.gift_for || '—'}</p></div>
          </div>
        )}
        {found === null && <p className="mt-3 text-sm text-gray-500">No certificate found.</p>}
      </Card>

      <Card className="p-4">
        <div className="mb-4 flex flex-col gap-3">
          <SearchInput value={search} onChange={setSearch} placeholder="Search certificate #, customer, recipient…" />
          <FilterBar onChange={setFilters} fields={[{ key: 'active', label: 'Active', column: 'is_active', type: 'boolean' }]} />
        </div>
        {loading ? <Spinner label="Loading gift certificates…" /> : (
          <>
            <DataTable
              rows={rows}
              sort={sort}
              onSort={toggleSort}
              columns={[
                { key: 'certificate_number', header: 'Certificate #', render: g => <button onClick={() => setEditing(g)} className="font-medium text-green-700 hover:underline">{g.certificate_number || '—'}</button>, sortKey: 'certificate_number' },
                { key: 'customer_name', header: 'Customer', render: g => g.customer_name || '—', sortKey: 'customer_name' },
                { key: 'current_balance', header: 'Balance', render: g => currency(g.current_balance), className: 'text-right', sortKey: 'current_balance' },
                { key: 'original_amount', header: 'Original', render: g => currency(g.original_amount), className: 'text-right', sortKey: 'original_amount' },
                { key: 'issue_date', header: 'Issued', render: g => date(g.issue_date), sortKey: 'issue_date' },
                { key: 'is_active', header: 'Status', render: g => g.is_active
                  ? <span className="px-2 py-0.5 rounded bg-green-100 text-green-700 text-xs">Active</span>
                  : <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-500 text-xs">Inactive</span> },
              ]}
            />
            <Pagination page={page} pageSize={PAGE_SIZE} total={count} onPage={setPage} />
          </>
        )}
      </Card>

      {editing && (
        <GiftCertificateModal
          existing={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload() }}
        />
      )}
    </div>
  )
}

// ---- Issue / edit gift certificate (frmGiftCertificates) ---------------------

function GiftCertificateModal({ existing, onClose, onSaved }: {
  existing: GiftCertificate | null
  onClose: () => void
  onSaved: () => void
}) {
  const { user } = useAuth()
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [custTerm, setCustTerm] = useState('')
  const [custResults, setCustResults] = useState<Customer[]>([])
  const [certNumber, setCertNumber] = useState(existing?.certificate_number ?? '')
  const [amount, setAmount] = useState(existing ? String(existing.current_balance ?? '') : '')
  const [redeemable, setRedeemable] = useState((existing?.redeemable as string) ?? "Thomson's")
  const [giftFor, setGiftFor] = useState(existing?.gift_for ?? '')
  const [giftFrom, setGiftFrom] = useState(existing?.gift_from ?? '')
  const [issueDate, setIssueDate] = useState((existing?.issue_date ?? new Date().toISOString()).slice(0, 10))
  const [expireDate, setExpireDate] = useState(existing?.expire_date ? String(existing.expire_date).slice(0, 10) : '')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!custTerm.trim()) { setCustResults([]); return }
    const t = setTimeout(async () => {
      try { const { rows } = await listCustomers({ search: custTerm, page: 0, pageSize: 8 }); setCustResults(rows) }
      catch { /* ignore */ }
    }, 250)
    return () => clearTimeout(t)
  }, [custTerm])

  const save = async () => {
    // Legacy frmGiftCertificates requires a customer for a NEW certificate.
    if (!existing && !customer) { toast.error('Gift Certificates must be assigned a customer. Please assign one.'); return }
    const amt = parseFloat(amount) || 0
    if (!existing && amt <= 0) { toast.error('Enter a certificate amount'); return }
    setSaving(true)
    try {
      if (existing) {
        await updateGiftCertificate(existing.id, {
          current_balance: amt,
          redeemable, gift_for: giftFor, gift_from: giftFrom,
          expire_date: expireDate || null,
        })
        toast.success('Gift Certificate updated')
      } else {
        await createGiftCertificate({
          certificate_number: certNumber.trim() || null,
          customer_id: customer!.id,
          customer_name: customerLabel(customer!),
          original_amount: amt,
          current_balance: amt,
          authorized_by_name: user?.email ?? null,
          redeemable,
          in_amount_of: amt,
          gift_for: giftFor || null,
          gift_from: giftFrom || null,
          issue_date: issueDate,
          expire_date: expireDate || null,
          is_active: true,
        })
        toast.success('Gift Certificate has been saved')
      }
      onSaved()
    } catch (e) {
      console.error(e)
      toast.error('Failed to save gift certificate')
    } finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg max-h-[88vh] overflow-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <h3 className="font-semibold">{existing ? `Edit Certificate ${existing.certificate_number ?? ''}` : 'Issue Gift Certificate'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-3">
          {!existing && (
            <div>
              <label className="text-sm font-medium text-gray-700">Customer <span className="text-red-500">*</span></label>
              {customer ? (
                <div className="mt-1 flex items-center justify-between rounded-lg border border-gray-200 px-3 py-2">
                  <span className="text-sm font-medium text-gray-800">{customerLabel(customer)}</span>
                  <button onClick={() => setCustomer(null)} className="text-gray-400 hover:text-red-600"><X size={15} /></button>
                </div>
              ) : (
                <div className="relative mt-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input value={custTerm} onChange={e => setCustTerm(e.target.value)} placeholder="Search customer…"
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 text-sm" />
                  {custResults.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-auto">
                      {custResults.map(c => (
                        <button key={c.id} onClick={() => { setCustomer(c); setCustTerm(''); setCustResults([]) }}
                          className="block w-full text-left px-3 py-2 hover:bg-green-50 text-sm border-b border-gray-50 last:border-0">
                          {customerLabel(c)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block text-sm"><span className="text-gray-600">Certificate #</span>
              <input value={certNumber} onChange={e => setCertNumber(e.target.value)} disabled={!!existing}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-100" /></label>
            <label className="block text-sm"><span className="text-gray-600">{existing ? 'Balance' : 'Amount'}</span>
              <input type="number" value={amount} onChange={e => setAmount(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Redeemable At</span>
              <input value={redeemable} onChange={e => setRedeemable(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">For / Reason</span>
              <input value={giftFor} onChange={e => setGiftFor(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">From</span>
              <input value={giftFrom} onChange={e => setGiftFrom(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
            <label className="block text-sm"><span className="text-gray-600">Issue Date</span>
              <input type="date" value={issueDate} onChange={e => setIssueDate(e.target.value)} disabled={!!existing}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300 disabled:bg-gray-100" /></label>
            <label className="block text-sm"><span className="text-gray-600">Expire Date</span>
              <input type="date" value={expireDate} onChange={e => setExpireDate(e.target.value)}
                className="mt-1 w-full px-3 py-2 rounded-lg border border-gray-300" /></label>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</Button>
        </div>
      </Card>
    </div>
  )
}
